import React, { useCallback, useState } from "react";

import { Button, Col, Form, Row } from "react-bootstrap";

import { Controller, useForm } from "react-hook-form";
import { useTypedParams } from "react-router-typesafe-routes/dom";
import { ROUTES } from "src/routes";

import {
  FinanceHold,
  IdCheckStatus,
  InternalUserInfo,
  InternalUserInfoFragment,
  InternalUserInfoInput,
  ParticipantTypes,
  useUpdateInternalUserInfoMutation,
} from "@api/graphql";
import { ToastMessage } from "@components/ToastMessage";
import { FormErrorText } from "@components/form/FormErrorText";
import { Loading } from "@components/loading";
import {
  DateFormat,
  formatStringToDateFormat,
  isAmpCmoArtCentre,
  isArtistBeneficiary,
  toFriendlyText,
} from "@util/index";

import { internalUserInfoErrorResolver } from "./internalUserInfoErrorResolver";

import styles from "./InternalUserInfoForm.module.scss";

interface InternalUserInfoProps {
  userInfo: InternalUserInfoFragment;
  setIsEditResale: (value: boolean) => void;
  setInternalUserData: (data: InternalUserInfo) => void;
  accountStatus?: string;
  participantTypes?: ParticipantTypes[];
}

export const InternalUserInfoForm: React.FC<InternalUserInfoProps> = ({
  userInfo,
  setIsEditResale,
  setInternalUserData,
  accountStatus,
  participantTypes,
}) => {
  const isAmpCmoArtCentreActive =
    isAmpCmoArtCentre(participantTypes) && (accountStatus === "ACTIVE" || accountStatus === "PENDING_APPROVAL");
  const isArtistBeneficiaryParticipant = isArtistBeneficiary(participantTypes);

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [updateInternalUserInfo, { loading, error }] = useUpdateInternalUserInfoMutation({});
  const { participantId } = useTypedParams(ROUTES.Profile.AccountDetails);

  const {
    control,
    getValues,
    formState: { errors },
    handleSubmit,
    watch,
    clearErrors,
    setError,
  } = useForm<InternalUserInfoInput>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: (data) => internalUserInfoErrorResolver(data, accountStatus, participantTypes),
    defaultValues: {
      ipi_basenumber: userInfo.ipi_basenumber,
      sam_id: userInfo.sam_id,
      id_check_status: userInfo.id_check_status,
      terms_accepted: userInfo.terms_accepted,
      account_restriction: userInfo.account_restriction,
      finance_hold: userInfo.finance_hold,
      supporting_doc_provided: userInfo.supporting_doc_provided,
    },
  });
  const financeHoldValue = watch("finance_hold");

  const isFinanceHoldRestricted = useCallback(
    (newStatus: FinanceHold) => {
      const restrictedFrom = [FinanceHold.BANK_DETAILS_REQUIRED, FinanceHold.BOUNCED_PAYMENT];
      const restrictedTo = [FinanceHold.DECEASED, FinanceHold.BANK_DETAILS_REQUIRED, FinanceHold.BOUNCED_PAYMENT];

      // Prevent switching to DECEASED unless done automatically
      if (newStatus === FinanceHold.DECEASED) {
        return true;
      }

      // Don't allow switching *from* restricted statuses
      if (restrictedFrom.includes(financeHoldValue) && financeHoldValue !== newStatus) {
        return true;
      }

      // Don't allow switching *to* restricted statuses
      return restrictedTo.includes(newStatus) && financeHoldValue !== newStatus;
    },
    [financeHoldValue],
  );

  const submitHandler = useCallback(async () => {
    try {
      const formData = getValues();
      const res = await updateInternalUserInfo({
        variables: {
          input: { ...formData, participant_id: participantId ?? -1 },
        },
      });
      const data = res?.data?.updateInternalUserInfo;
      if (data) {
        setToastMessage(`Intenal use info updated successfully.`);
        setInternalUserData({
          ipi_basenumber: formData.ipi_basenumber,
          sam_id: formData.sam_id,
          id_check_status: formData.id_check_status,
          id_check_time: userInfo.id_check_time,
          terms_accepted: formData.terms_accepted,
          terms_accepted_time: userInfo.terms_accepted_time,
          account_restriction: formData.account_restriction,
          finance_hold: formData.finance_hold,
          supporting_doc_provided: formData.supporting_doc_provided,
          parent_account: userInfo.parent_account,
        });
        setShowToast(true);
        setIsEditResale(false);
      } else {
        setToastMessage(`Error updating internal use info.`);
        setShowToast(true);
      }
    } catch (error) {
      setToastMessage(`Error updating internal use info ${error}`);
      setShowToast(true);
    }
  }, [getValues, participantId, setInternalUserData, setIsEditResale, updateInternalUserInfo, userInfo]);

  const handleContentClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div className={styles["form-section"]}>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type="error" />
      {loading && <Loading />}
      <div onClick={handleContentClick}>
        <Form onSubmit={handleSubmit(submitHandler)}>
          <Row className="mb-4">
            <Col>
              <Form.Group>
                <Form.Label>IPI number</Form.Label>
                <Controller
                  name="ipi_basenumber"
                  control={control}
                  render={({ field }) => <Form.Control {...field} placeholder="Please enter IPI number" />}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>SAM ID</Form.Label>
                <Controller
                  name="sam_id"
                  control={control}
                  render={({ field }) => <Form.Control {...field} placeholder="Please enter IPI number" />}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              <Form.Group>
                <Form.Label>ID verification status</Form.Label>
                {userInfo.id_check_status === IdCheckStatus.COMPLETE ||
                userInfo.id_check_status === IdCheckStatus.MANUAL_CHECK ? (
                  <div>{toFriendlyText(userInfo.id_check_status)}</div>
                ) : (
                  <Controller
                    name="id_check_status"
                    control={control}
                    render={({ field }) => (
                      <Form.Control {...field} as="select" isInvalid={!!errors?.id_check_status}>
                        <option value="">Select your title</option>
                        <option value="INCOMPLETE">Incomplete</option>
                        <option value="MANUAL_CHECK">Manual check</option>
                      </Form.Control>
                    )}
                  />
                )}
                <FormErrorText error={errors?.id_check_status} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>ID verification date/time</Form.Label>
                <div>
                  {userInfo.id_check_time
                    ? formatStringToDateFormat(userInfo.id_check_time, DateFormat.DD_MM_YYYY_SLASH)
                    : "-"}
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              <Form.Group>
                <Form.Label>Terms & conditions</Form.Label>
                <Controller
                  name="terms_accepted"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as="select"
                      value={field.value?.toString()}
                      onChange={(e) => field.onChange(e.target.value === "true")}
                      isInvalid={!!errors?.terms_accepted}
                    >
                      <option value="true">Accepted</option>
                      <option value="false">Not accepted</option>
                    </Form.Control>
                  )}
                />
                <FormErrorText error={errors?.terms_accepted} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Terms & conditions date/time</Form.Label>
                <div>
                  {userInfo.terms_accepted_time
                    ? formatStringToDateFormat(userInfo.terms_accepted_time, DateFormat.DD_MM_YYYY_SLASH)
                    : "-"}
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              <Form.Group>
                <Form.Label>Red flag account</Form.Label>
                <Controller
                  name="account_restriction"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as="select"
                      value={field.value?.toString()}
                      onChange={(e) => field.onChange(e.target.value === "true")}
                    >
                      <option value=""> Please select</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Control>
                  )}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Parent account</Form.Label>
                <div>{userInfo.parent_account || "-"}</div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              <Form.Group>
                <Form.Label>Supporting documentation sighted</Form.Label>
                {userInfo.supporting_doc_provided === true ? (
                  <div>Yes</div>
                ) : (
                  <Controller
                    name="supporting_doc_provided"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        as="select"
                        value={field.value?.toString()}
                        onChange={(e) => field.onChange(e.target.value === "true")}
                      >
                        <option value=""> Please select</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </Form.Control>
                    )}
                  />
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>
                  {isAmpCmoArtCentreActive || isArtistBeneficiaryParticipant ? "* " : ""}
                  Bank account hold
                </Form.Label>
                <Controller
                  name="finance_hold"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as="select"
                      value={field.value}
                      onChange={(e) => {
                        const newValue = e.target.value as FinanceHold;
                        if (!isFinanceHoldRestricted(newValue)) {
                          field.onChange(newValue);
                          clearErrors("finance_hold");
                        } else {
                          setError("finance_hold", {
                            type: "required",
                            message: `Cannot change finance hold status to "${newValue}"`,
                          });
                          setToastMessage(`Cannot change finance hold status to "${newValue}"`);
                          setShowToast(true);
                        }
                      }}
                      isInvalid={!!errors?.finance_hold}
                    >
                      <option value=""> Please select</option>
                      <option value={FinanceHold.BANK_DETAILS_REQUIRED}>Bank details required</option>
                      <option value={FinanceHold.BOUNCED_PAYMENT}>Bounced payment</option>
                      <option value={FinanceHold.DECEASED}>Deceased</option>
                      <option value={FinanceHold.ENTITLEMENT_ISSUES}>Entitlement issues</option>
                      <option value={FinanceHold.INCARCERATED}>Incarcerated</option>
                      <option value={FinanceHold.NO_HOLD}>No hold</option>
                      <option value={FinanceHold.OTHER}>Other</option>
                    </Form.Control>
                  )}
                />
                <FormErrorText error={errors?.finance_hold} />
              </Form.Group>
            </Col>
          </Row>
          {error && (
            <Row className="mb-4">
              <FormErrorText error={error.message} />
            </Row>
          )}

          <Row className="mt-3">
            <Col className="d-flex justify-content-end">
              <Button variant="secondary" onClick={() => setIsEditResale(false)} className="me-2">
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};
